/**
 * useReservePay — singleton hook for ReservePay browser SDK
 *
 * Initialises window.Reservepay once and exposes a stable instance.
 * The SDK script is loaded via <script> in index.html before the React bundle.
 */

import { useEffect, useRef } from 'react'

const MERCHANT_ID = import.meta.env.VITE_RESERVEPAY_MERCHANT_ID || '115812364394'
const INSTALLATION_ID = import.meta.env.VITE_RESERVEPAY_INSTALLATION_ID || 'ins_BNvKE7xaQK'
const SELECT_TIMEOUT_MS = Number(import.meta.env.VITE_RESERVEPAY_SELECT_TIMEOUT_MS || 15000)
const ENABLE_DIRECT_FALLBACK = import.meta.env.VITE_RESERVEPAY_ENABLE_DIRECT_FALLBACK === 'true'
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

let sdkInstance: ReturnType<NonNullable<Window['Reservepay']>['initReservepay']> | null = null

function normalizeThaiMobile(input?: string): string | undefined {
  if (!input) return undefined
  const digits = input.replace(/\D/g, '')
  if (!digits) return undefined

  if (digits.startsWith('66') && digits.length === 11) {
    return `+${digits}`
  }
  if (digits.startsWith('0') && digits.length === 10) {
    return `+66${digits.slice(1)}`
  }
  if (digits.startsWith('9') && digits.length === 9) {
    return `+66${digits}`
  }
  if (input.startsWith('+')) {
    return input
  }
  return undefined
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, context: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(`ReservePay ${context} timeout after ${timeoutMs}ms`))
    }, timeoutMs)

    promise
      .then((value) => {
        window.clearTimeout(timer)
        resolve(value)
      })
      .catch((error) => {
        window.clearTimeout(timer)
        reject(error)
      })
  })
}

function getOrInitSdk() {
  if (sdkInstance) return sdkInstance
  if (typeof window === 'undefined' || !window.Reservepay) return null
  sdkInstance = window.Reservepay.initReservepay({
    merchantId: MERCHANT_ID,
    installationId: INSTALLATION_ID,
  })
  return sdkInstance
}

async function backendSelectPaymentMethod(payload: {
  amount: string
  currency: string
  payment_method: 'PROMPTPAY'
  customer_email?: string
  customer_mobile?: string
  customer_name?: string
}): Promise<string | null> {
  const token = localStorage.getItem('authToken')
  if (!token) return null

  const res = await fetch(`${API_BASE_URL}/api/payment/user/reservepay/select-payment-method`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      merchant_id: MERCHANT_ID,
      installation_id: INSTALLATION_ID,
      ...payload,
    }),
  })

  const data = await res.json().catch(() => null)
  if (!res.ok || !data?.success) {
    const msg = data?.error || `backend select-payment-method failed (${res.status})`
    throw new Error(msg)
  }

  const sessionId = data?.data?.payment_session_id
  return typeof sessionId === 'string' ? sessionId : null
}

async function backendDiscoverNextAction(payload: {
  payment_session_id: string
  pollingUntilAction: 'DISPLAY_QR' | 'CHECK_STATUS' | 'REDIRECT'
  maxWaitMs?: number
}): Promise<{ action: string; redirectUrl?: string } | null> {
  const token = localStorage.getItem('authToken')
  if (!token) return null

  const res = await fetch(`${API_BASE_URL}/api/payment/user/reservepay/discover-next-action`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      merchant_id: MERCHANT_ID,
      installation_id: INSTALLATION_ID,
      ...payload,
    }),
  })

  const data = await res.json().catch(() => null)
  if (!res.ok || !data?.success) {
    const msg = data?.error || `backend discover-next-action failed (${res.status})`
    throw new Error(msg)
  }

  return {
    action: data?.data?.action,
    redirectUrl: data?.data?.redirect_url || undefined,
  }
}

async function backendRetrieveQrData(payload: {
  payment_session_id: string
}): Promise<string | null> {
  const token = localStorage.getItem('authToken')
  if (!token) return null

  const res = await fetch(`${API_BASE_URL}/api/payment/user/reservepay/retrieve-qr-data`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      merchant_id: MERCHANT_ID,
      installation_id: INSTALLATION_ID,
      ...payload,
    }),
  })

  const data = await res.json().catch(() => null)
  if (!res.ok || !data?.success) {
    const msg = data?.error || `backend retrieve-qr-data failed (${res.status})`
    throw new Error(msg)
  }

  const qrString = data?.data?.qr_string
  return typeof qrString === 'string' ? qrString : null
}

async function directSelectPaymentMethod(payload: {
  amount: string
  currency: string
  payment_method: 'PROMPTPAY'
  customer_email?: string
  customer_mobile?: string
  customer_name?: string
}): Promise<string> {
  const res = await fetch('https://api.reservepay.com/sdk/select-payment-method', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      merchant_id: MERCHANT_ID,
      installation_id: INSTALLATION_ID,
      ...payload,
    }),
  })

  const data = await res.json().catch(() => null)

  // ReservePay SDK endpoints usually return a plain string payment_session_id on success.
  if (res.ok && typeof data === 'string' && data.length > 0) {
    return data
  }

  if (res.ok && data && typeof data.payment_session_id === 'string') {
    return data.payment_session_id
  }

  if (Array.isArray(data) && data.length >= 2) {
    throw new Error(`ReservePay direct API ${data[0]}: ${data[1]}`)
  }

  throw new Error(`ReservePay direct API failed (${res.status})`)
}

async function backendCheckTransactionStatus(transactionId: string): Promise<string | null> {
  const token = localStorage.getItem('authToken')
  if (!token || !transactionId) return null

  const res = await fetch(`${API_BASE_URL}/api/payment/user/status/${transactionId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  const data = await res.json().catch(() => null)
  const status = data?.data?.transaction?.status
  if (status === 'completed') return 'SUCCESSFUL'
  if (status === 'failed' || status === 'cancelled' || status === 'expired') return 'FAILED'
  return null
}

async function backendTokenizeCard(payload: {
  number: string
  exp_month: string
  exp_year: string
  cvc: string
  name?: string
}): Promise<string> {
  const token = localStorage.getItem('authToken')
  if (!token) throw new Error('Authentication required')

  const res = await fetch(`${API_BASE_URL}/api/payment/user/reservepay/tokenize-card`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      merchant_id: MERCHANT_ID,
      installation_id: INSTALLATION_ID,
      ...payload,
    }),
  })

  const data = await res.json().catch(() => null)
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || `tokenize-card failed (${res.status})`)
  }

  return data?.data?.token
}

async function backendSelectCardPayment(payload: {
  amount: string
  currency: string
  token: string
}): Promise<string | null> {
  const token = localStorage.getItem('authToken')
  if (!token) return null

  const res = await fetch(`${API_BASE_URL}/api/payment/user/reservepay/select-payment-method`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      merchant_id: MERCHANT_ID,
      installation_id: INSTALLATION_ID,
      amount: payload.amount,
      currency: payload.currency,
      payment_method: 'CARD',
      token: payload.token,
    }),
  })

  const data = await res.json().catch(() => null)
  return data?.data?.payment_session_id || null
}

export function useReservePay() {
  const instanceRef = useRef(getOrInitSdk())

  useEffect(() => {
    // Re-try init if SDK script hadn't loaded yet on first render
    if (!instanceRef.current) {
      instanceRef.current = getOrInitSdk()
    }
  }, [])

  /**
   * Select PromptPay as payment method and return payment_session_id.
   * Amount is in THB (will be converted to satang).
   * Also returns the internal transaction ID for later status polling.
   */
  async function selectPromptPay(
    amountTHB: number,
    customer?: { email?: string; mobile?: string; name?: string }
  ): Promise<{ paymentSessionId: string; transactionId?: string }> {
    const sdk = instanceRef.current ?? getOrInitSdk()
    if (!sdk) throw new Error('ReservePay SDK ยังไม่พร้อม กรุณารีเฟรชหน้า')
    const amountSatang = String(Math.round(amountTHB * 100))

    const normalizedMobile = normalizeThaiMobile(customer?.mobile)
    const requestWithCustomer = {
      amount: amountSatang,
      currency: 'THB',
      payment_method: 'PROMPTPAY' as const,
      ...(customer?.email ? { customer_email: customer.email } : {}),
      ...(normalizedMobile ? { customer_mobile: normalizedMobile } : {}),
      ...(customer?.name ? { customer_name: customer.name } : {}),
    }
    const requestBasic = {
      amount: amountSatang,
      currency: 'THB',
      payment_method: 'PROMPTPAY' as const,
    }

    try {
      const backendSessionId = await withTimeout(
        backendSelectPaymentMethod({
          amount: amountSatang,
          currency: 'THB',
          payment_method: 'PROMPTPAY',
          ...(customer?.email ? { customer_email: customer.email } : {}),
          ...(normalizedMobile ? { customer_mobile: normalizedMobile } : {}),
          ...(customer?.name ? { customer_name: customer.name } : {}),
        }),
        SELECT_TIMEOUT_MS,
        'backend select-payment-method'
      )
      if (backendSessionId) return { paymentSessionId: backendSessionId }
    } catch {
      // Fall through to browser SDK path.
    }

    try {
      return await withTimeout(
        sdk.endpoints.sdk.selectPaymentMethod(requestWithCustomer),
        SELECT_TIMEOUT_MS,
        'selectPaymentMethod(PROMPTPAY)'
      ).then((id) => ({ paymentSessionId: id }))
    } catch (firstError) {
      // Reset singleton and retry once in case SDK transport got into a bad state.
      sdkInstance = null
      const retrySdk = getOrInitSdk()
      if (!retrySdk) {
        throw new Error('ReservePay SDK ยังไม่พร้อม กรุณารีเฟรชหน้า')
      }

      try {
        return await withTimeout(
          retrySdk.endpoints.sdk.selectPaymentMethod(requestBasic),
          SELECT_TIMEOUT_MS,
          'selectPaymentMethod(PROMPTPAY) retry(no-customer-fields)'
        ).then((id) => ({ paymentSessionId: id }))
      } catch (secondError: any) {
        if (!ENABLE_DIRECT_FALLBACK) {
          const reason =
            secondError?.message ||
            (firstError instanceof Error ? firstError.message : 'unknown')
          throw new Error(
            `ไม่สามารถเริ่ม PromptPay ได้ (${reason}). โปรดตรวจสอบ Installation (domain/payment method/customer capture) ให้ตรงกับ dev.slipaj.com`
          )
        }

        try {
          return await withTimeout(
            directSelectPaymentMethod({
              amount: amountSatang,
              currency: 'THB',
              payment_method: 'PROMPTPAY',
              ...(customer?.email ? { customer_email: customer.email } : {}),
              ...(normalizedMobile ? { customer_mobile: normalizedMobile } : {}),
              ...(customer?.name ? { customer_name: customer.name } : {}),
            }),
            SELECT_TIMEOUT_MS,
            'direct select-payment-method'
          ).then((id) => ({ paymentSessionId: id }))
        } catch (thirdError: any) {
          const thirdMsg = thirdError?.message
          const normalizedThird =
            thirdMsg === 'Failed to fetch'
              ? 'direct fallback blocked (CORS/network)'
              : thirdMsg
          const reason =
            normalizedThird ||
            secondError?.message ||
            (firstError instanceof Error ? firstError.message : 'unknown')
          throw new Error(
            `ไม่สามารถเริ่ม PromptPay ได้ (${reason}). โปรดตรวจสอบ Installation (domain/payment method/customer capture) ให้ตรงกับ dev.slipaj.com`
          )
        }
      }
    }
  }

  /** Poll until discover-next-action returns DISPLAY_QR, then get QR string. */
  async function waitForQR(
    paymentSessionId: string,
    signal?: AbortSignal
  ): Promise<{ polling: unknown; qrString: string }> {
    try {
      const result = await withTimeout(
        backendDiscoverNextAction({
          payment_session_id: paymentSessionId,
          pollingUntilAction: 'DISPLAY_QR',
          maxWaitMs: 45000,
        }),
        50000,
        'backend discover-next-action(DISPLAY_QR)'
      )

      if (result?.action === 'DISPLAY_QR') {
        const qrString = await withTimeout(
          backendRetrieveQrData({ payment_session_id: paymentSessionId }),
          20000,
          'backend retrieve-qr-data'
        )

        if (qrString) {
          return { polling: null, qrString }
        }
      }
    } catch {
      // Fall back to browser SDK polling path.
    }

    const sdk = instanceRef.current ?? getOrInitSdk()
    if (!sdk) throw new Error('ReservePay SDK ยังไม่พร้อม')

    const polling = sdk.endpoints.sdk.createDiscoverNextActionPolling()
    if (signal) signal.addEventListener('abort', () => polling.abort())

    await polling.execute({ payment_session_id: paymentSessionId, pollingUntilAction: 'DISPLAY_QR' })
    const qrBase64 = await sdk.endpoints.sdk.retrieveQrData({ payment_session_id: paymentSessionId })
    // Decode base64 → raw QR payload string
    const qrString = atob(qrBase64)
    return { polling, qrString }
  }

  /** Poll until discover-next-action returns CHECK_STATUS, then check final status. */
  /**
   * Wait for payment completion by polling our own transaction status.
   * The ReservePay webhook updates the DB → our API reflects it → frontend detects it.
   * No SDK dependency — simpler, faster, more reliable.
   */
  async function waitForCompletion(
    _paymentSessionId: string,
    transactionId?: string,
    signal?: AbortSignal
  ): Promise<string> {
    if (!transactionId) throw new Error('transactionId is required')

    // Check immediately first — webhook may have already arrived
    let status = await backendCheckTransactionStatus(transactionId)
    if (status) return status

    // Poll every 3s until complete or aborted
    while (true) {
      if (signal?.aborted) throw new Error('Aborted')
      await new Promise((r) => setTimeout(r, 3000))
      status = await backendCheckTransactionStatus(transactionId)
      if (status) return status
    }
  }

  async function tokenizeCard(payload: { number: string; exp_month: string; exp_year: string; cvc: string; name?: string }): Promise<string> {
    return await backendTokenizeCard(payload)
  }

  async function selectCardPayment(amountTHB: number, cardToken: string): Promise<{ paymentSessionId: string }> {
    const amountSatang = String(Math.round(amountTHB * 100))
    const sessionId = await backendSelectCardPayment({ amount: amountSatang, currency: 'THB', token: cardToken })
    if (!sessionId) throw new Error('Failed to select CARD payment method')
    return { paymentSessionId: sessionId }
  }

  /**
   * Wait for discover-next-action specifically for CARD flow.
   * - REDIRECT → 3D Secure needed, returns redirect URL
   * - CHECK_STATUS → no 3DS, proceed to status polling
   */
  async function waitForCardAction(
    paymentSessionId: string
  ): Promise<{ action: 'REDIRECT'; redirectUrl: string } | { action: 'CHECK_STATUS' }> {
    const result = await withTimeout(
      backendDiscoverNextAction({
        payment_session_id: paymentSessionId,
        pollingUntilAction: 'CHECK_STATUS',
        maxWaitMs: 45000,
      }),
      50000,
      'backend discover-next-action(CARD)'
    )

    if (!result) throw new Error('discover-next-action returned null')

    if (result.action === 'REDIRECT' && result.redirectUrl) {
      return { action: 'REDIRECT', redirectUrl: result.redirectUrl }
    }

    if (result.action === 'CHECK_STATUS') {
      return { action: 'CHECK_STATUS' }
    }

    throw new Error(`Unexpected action from discover-next-action: ${result.action}`)
  }

  return { selectPromptPay, waitForQR, waitForCompletion, tokenizeCard, selectCardPayment, waitForCardAction }
}
